import React, { useMemo } from 'react';
import { useAnalytics, TimeFilter, ChartType } from '@Context/AnalyticsContext';
import { AIFeatureKeys } from '@Models/types';

interface Option<T> {
  label: string;
  value: T;
}

const TIME_OPTIONS: Option<TimeFilter>[] = [
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'all', label: 'All Time' },
];

const CHART_TYPE_OPTIONS: Option<ChartType>[] = [
  { value: 'line', label: 'Line Chart' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'pie', label: 'Pie Chart' },
];

const AnalyticsFilters: React.FC = () => {
  const {
    filters,
    chartType,
    setFilters,
    setChartType,
    availableUsers,
    availableModels,
    modelOptions,
    availableFeatures,
    loading,
  } = useAnalytics();

  const userOptions = useMemo(
    (): Option<string>[] => [
      { value: '', label: 'All Users' },
      ...availableUsers.map((user) => ({ value: user, label: user })),
    ],
    [availableUsers]
  );

  const modelDropdownOptions = useMemo((): Option<string>[] => {
    const options: Option<string>[] = [{ value: '', label: 'All Models' }];

    availableModels.forEach((id) => {
      const name = modelOptions[id] ?? id;
      const label = name.replace(/^us\.(anthropic|amazon)\./, '');
      options.push({ value: id, label });
    });

    return options;
  }, [availableModels, modelOptions]);

  const featureOptions = useMemo(
    (): Option<string>[] => [
      { value: '', label: 'All Features' },
      ...availableFeatures.map((feature) => ({
        value: feature,
        label: feature
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase()),
      })),
    ],
    [availableFeatures]
  );

  return (
    <div className="space-y-4">
      {/* Primary Filters Row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Time Period
          </label>
          <select
            value={filters.time}
            onChange={(e) => setFilters({ time: e.target.value as TimeFilter })}
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {TIME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Chart Type
          </label>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value as ChartType)}
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {CHART_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            User
          </label>
          <select
            value={filters.user || ''}
            onChange={(e) => setFilters({ user: e.target.value || undefined })}
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {userOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Feature
          </label>
          <select
            value={filters.feature || ''}
            onChange={(e) =>
              setFilters({
                feature: (e.target.value as AIFeatureKeys) || undefined,
              })
            }
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {featureOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Model
          </label>
          <select
            value={filters.model || ''}
            onChange={(e) => setFilters({ model: e.target.value || undefined })}
            disabled={loading}
            className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {modelDropdownOptions.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilters;
